/* eslint import/no-cycle: off */
import { OffersService } from './offers.service.js';

class _OffersMetaService {
  /**
   * Adds some contextual information to offers about how they relate to other
   * offers or some other information about the applicant.
   * offer.meta = {
   *   term: // How long this term is compared to others or the current loan.
   *         // Values: "SAME", "LONGER", "LONGEST", "SHORTER", "SHORTEST"
   *   payment: // How this offer payment compares to the current loan.
   *            // Values: "SAME", "LOWER", "HIGHER"
   *   apr: // How this apr compares to the current loan.
   *        // Values: "SAME", "LOWER", "HIGHER", "UNKNOWN"
   *   lifetimeSavings: // "POSITIVE" if savings is >-1000 and "NEGATIVE" if not
   * }
   */
  addContextToOffers(offers) {
    this.offers = offers;
    this.currentLoan = OffersService.currentLoan;

    for (const offer of this.offers) {
      offer.meta = {
        term: this._getTermKey(offer),
        payment: this._getPaymentKey(offer),
        apr: this._getAPRKey(offer),
        lifetimeSavings: this._getLifetimeSavingsKey(offer),
        overallSavings: this._getOverallSaverKey(offer),
      };
    }
  }

  sortOffers(offers) {
    // Sort the offers in descending term order.
    const firstOffer = OffersService.firstPossibleOffer(offers);
    const result = Array.from(offers);

    result.sort((a, b) => {
      if (a.offerId === firstOffer.offerId) return -1;
      if (b.offerId === firstOffer.offerId) return 1;
      return b.term - a.term;
    });

    return result;
  }

  _getTermKey(offer) {
    if (offer.term === this.currentLoan.remainingPayment) {
      return 'SAME';
    }
    if (offer.term > this.currentLoan.remainingPayment) {
      let termKey = 'LONGEST';
      for (const otherOffer of this.offers) {
        if (offer.term < otherOffer.term) {
          termKey = 'LONGER';
          break;
        }
      }

      return termKey;
    }
    let termKey = 'SHORTEST';
    for (const otherOffer of this.offers) {
      if (offer.term > otherOffer.term) {
        termKey = 'SHORTER';
      }
    }

    return termKey;
  }

  _getPaymentKey(offer) {
    if (offer.offerPayment === this.currentLoan.currentMonthlyPayment) {
      return 'SAME';
    }
    if (offer.offerPayment > this.currentLoan.currentMonthlyPayment) {
      return 'HIGHER';
    }
    return 'LOWER';
  }

  _getAPRKey(offer) {
    const apr = offer.offerApr;

    if (!this.currentLoan.apr) {
      return 'UNKNOWN';
    }
    if (apr === this.currentLoan.apr) {
      return 'SAME';
    }
    if (apr > this.currentLoan.apr) {
      return 'HIGHER';
    }
    return 'LOWER';
  }

  _getLifetimeSavingsKey(offer) {
    if (offer.savings.lifetimeSavings > 0) {
      return 'POSITIVE';
    }

    return 'NEGATIVE';
  }

  _getOverallSaverKey(offer) {
    if (offer.savings.lifetimeSavings > -1000 && offer.savings.monthlySavings > 0) {
      return 'POSITIVE';
    }

    return 'NEGATIVE';
  }
}

export const OffersMetaService = new _OffersMetaService();
